import { useCallback, useEffect, useState } from 'react'
import {
  getAgendaProximosDias,
  getCaixaDoMes,
  getCronograma,
  getDecisoes,
  getRitmoObra,
  getSemanaDaObra,
  decisoesQueTravemAgora,
  resumirCronograma,
  type AgendaItem,
  type CaixaDoMes,
  type CronogramaResumo,
  type RitmoObra,
  type SemanaDaObra,
} from '@services/gestao.js'
import type { DecisaoCatalogo } from '@content/decisoes-catalogo.js'
import { countObraPendenciasAbertas, getObraConfig } from '@services/crud.js'
import { ROUTES, contratoDetailPath } from '@utils/constants.js'

export interface HojeNaObra {
  agenda: AgendaItem[]
  cronograma: CronogramaResumo
  semana: SemanaDaObra
  caixa: CaixaDoMes
  ritmo: RitmoObra
  decisoesTravando: DecisaoCatalogo[]
  pendenciasAbertas: number
  isLoading: boolean
  reload: () => Promise<void>
}

const VAZIO: CronogramaResumo = {
  avancoGeral: 0,
  diasDeObra: null,
  fasesAtrasadas: [],
  faseAtualId: null,
  terminoPrevisto: null,
  atrasoDias: 0,
}

const SEMANA_VAZIA: SemanaDaObra = {
  saidaLiberada: 0,
  valorSemMedir: 0,
  aMedir: [],
  aPagar: [],
  fotosNaSemana: 0,
  prova: null,
}

const CAIXA_VAZIA: CaixaDoMes = {
  mesLabel: '',
  anoMes: '',
  saidaLiberada: 0,
  saidaSemMedir: 0,
  gastoMes: 0,
  reservaIntacta: null,
  aposPagar: null,
  comeReserva: false,
}

const RITMO_VAZIO: RitmoObra = {
  diasSemDiario: null,
  ultimaData: null,
  parado: false,
  chuvasNaSemana: 0,
}

/** Agrega o que o dono da obra precisa ver ao abrir o app: agenda, avanço e pendências. */
export function useHojeNaObra(dias = 7): HojeNaObra {
  const [agenda, setAgenda] = useState<AgendaItem[]>([])
  const [cronograma, setCronograma] = useState<CronogramaResumo>(VAZIO)
  const [semana, setSemana] = useState<SemanaDaObra>(SEMANA_VAZIA)
  const [caixa, setCaixa] = useState<CaixaDoMes>(CAIXA_VAZIA)
  const [ritmo, setRitmo] = useState<RitmoObra>(RITMO_VAZIO)
  const [decisoesTravando, setDecisoesTravando] = useState<DecisaoCatalogo[]>([])
  const [pendenciasAbertas, setPendenciasAbertas] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const [itens, fases, config, pend, semanaObra, caixaMes, ritmoObra, decisoes] =
      await Promise.all([
        getAgendaProximosDias(dias, {
          contrato: contratoDetailPath,
          pendencias: ROUTES.PENDENCIAS,
          cronograma: ROUTES.CRONOGRAMA,
        }),
        getCronograma(),
        getObraConfig(),
        countObraPendenciasAbertas(),
        getSemanaDaObra(),
        getCaixaDoMes(),
        getRitmoObra(),
        getDecisoes(),
      ])
    const resumo = resumirCronograma(fases, config?.dataInicio, config?.dataPrevisaoTermino)
    setAgenda(itens)
    setCronograma(resumo)
    setSemana(semanaObra)
    setCaixa(caixaMes)
    setRitmo(ritmoObra)
    setDecisoesTravando(
      decisoesQueTravemAgora(decisoes, resumo.faseAtualId ?? config?.faseConstrutiva ?? null),
    )
    setPendenciasAbertas(pend)
    setIsLoading(false)
  }, [dias])

  useEffect(() => {
    load()
  }, [load])

  return {
    agenda,
    cronograma,
    semana,
    caixa,
    ritmo,
    decisoesTravando,
    pendenciasAbertas,
    isLoading,
    reload: load,
  }
}
